import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import skillenLogo from "@/assets/skillenLogo.png";

/* ----------------------------- Logo Component ----------------------------- */

export function Logo({ light = false }) {
  return (
    <Link
      to="/"
      className="flex items-center transition-transform duration-300 hover:scale-105"
    >
      <img
        src={skillenLogo}
        alt="SkillEn"
        className="h-14 lg:h-16 w-auto object-contain bg-white rounded-md px-3 py-2 shadow-sm"
      />
    </Link>
  );
}

/* ----------------------------- Navigation ----------------------------- */

const PRIMARY_NAV = [
  { to: "/", label: "Home" },
  { to: "/students", label: "Students" },
  { to: "/companies", label: "Companies" },
  { to: "/colleges", label: "Colleges" },
  { to: "/courses", label: "Courses" },
];

const MORE_NAV = [
  { to: "/jobs", label: "Jobs" },
  { to: "/mock-interviews", label: "Mock Interviews" },
  { to: "/success-stories", label: "Success" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

/* ----------------------------- Header ----------------------------- */

export default function Header() {
  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const moreActive = MORE_NAV.some((item) => item.to === pathname);

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#06252C]/95 backdrop-blur-lg border-b border-white/10 shadow-lg"
          : "bg-[#06252C]"
      }`} 
    >
      <div className="mx-auto flex h-[92px] max-w-7xl items-center justify-between px-5 lg:px-8 xl:px-10">

        {/* Logo */}

        <Logo />

        {/* Desktop Navigation */}

        <nav className="hidden lg:flex items-center gap-4 xl:gap-5">

          {PRIMARY_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-[#F26C21]"
                    : "text-white/85 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {item.label}

                  {isActive && (
                    <span className="absolute left-0 -bottom-2 h-[2px] w-full rounded bg-[#F26C21]" />
                  )}
                </span>
              )}
            </NavLink>
          ))}

          {/* More Dropdown */}

          <div
            className="relative"
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
     
  <button
  className={`relative flex items-center gap-1 text-sm font-medium transition ${
    moreActive
      ? "text-[#F26C21]"
      : "text-white/85 hover:text-white"
  }`}
>
  More

  <ChevronDown
    size={16}
    className={`transition-transform duration-200 ${
      moreOpen ? "rotate-180" : ""
    }`}
  />

  {moreActive && (
    <span className="absolute left-0 -bottom-2 h-[2px] w-full rounded bg-[#F26C21]" />
  )}
</button>

            {moreOpen && (
  <div className="absolute left-0 top-full pt-4 w-60">
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white py-2 shadow-2xl animate-in fade-in zoom-in-95 duration-150">
      {MORE_NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            `block px-5 py-3 text-sm transition ${
              isActive
                ? "bg-orange-50 font-semibold text-[#F26C21]"
                : "text-[#06252C] hover:bg-gray-50"
            }`
          }
        >
          {item.label}
        </NavLink>
      ))}
    </div>
  </div>
)}
 </div>

          {/* Explore Button */}

          <Link
            to="/courses"
            className="ml-3 inline-flex items-center rounded-md bg-[#F26C21] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-[#FF6600]"
          >
            Explore Programs →
          </Link>

        </nav>

        {/* Mobile Menu Button */}

        <button
          onClick={() => setOpen(!open)}
          className="rounded-md p-2 text-white transition hover:bg-white/10 lg:hidden"
          aria-label="Toggle Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
	        {/* Mobile Navigation */}

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#06252C] shadow-xl">
          <div className="flex flex-col px-5 py-4">

            {[...PRIMARY_NAV, ...MORE_NAV].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-2 py-3 text-base transition-colors ${
                    isActive
                      ? "bg-white/10 font-semibold text-[#F26C21]"
                      : "text-white/85 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}

            <Link
              to="/courses"
              onClick={() => setOpen(false)}
              className="mt-5 inline-flex items-center justify-center rounded-md bg-[#F26C21] px-5 py-3 font-semibold text-white transition hover:bg-[#FF6600]"
            >
              Explore Programs →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}